// Content filter + runtime takedown / kill-switch (best-practice §4.4).
//
// Two concerns, split for performance:
//  1. CONTENT FILTER (ecchi) — bundled as STATIC_* constants so it applies instantly
//     at first render with NO network fetch (keeps killswitch.json off the critical
//     render path → better LCP/FCP).
//  2. LEGAL TAKEDOWN — /killswitch.json fetched asynchronously AFTER first paint; can
//     flip `global`, disable a `mode`, or add extra banned ids without a rebuild.
//     Edits take effect on the next load (json is not precached, max-age=0).

export interface KillSwitch {
  global: boolean;
  modes: Record<string, boolean>;
  bannedAnime: number[];
  bannedCharacters: number[];
}

// Bundled ecchi/fan-service denylist (mainstream mild-ecchi intentionally kept).
const STATIC_ANIME = [
  11617, 15451, 20745, 97767, 8074, 20807, 103139, 20910, 101004, 20631, 21685,
  21701, 21093, 20678, 141949, 108241, 115113, 97863, 17729, 21290, 3455, 101168,
  21306, 226, 21092, 21131, 1195,
];
const STATIC_CHARS = [50389, 51347, 7373, 127596];

const DEFAULT: KillSwitch = { global: false, modes: {}, bannedAnime: [], bannedCharacters: [] };

let current: KillSwitch = DEFAULT;
let bannedAnimeSet = new Set<number>(STATIC_ANIME);
let bannedCharSet = new Set<number>(STATIC_CHARS);

// Non-blocking: call after first render. Merges live takedown config over the static
// content filter, then notifies the app so global/mode kills can apply.
export async function loadKillSwitch(): Promise<void> {
  try {
    const res = await fetch('/killswitch.json', { cache: 'no-store' });
    if (!res.ok) return;
    const data = (await res.json()) as Partial<KillSwitch>;
    current = {
      global: !!data.global,
      modes: data.modes ?? {},
      bannedAnime: data.bannedAnime ?? [],
      bannedCharacters: data.bannedCharacters ?? [],
    };
    bannedAnimeSet = new Set([...STATIC_ANIME, ...current.bannedAnime]);
    bannedCharSet = new Set([...STATIC_CHARS, ...current.bannedCharacters]);
  } catch {
    /* network/parse failure → keep static defaults; game still works */
  } finally {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('animedle:killswitch'));
  }
}

export function isGlobalKilled(): boolean {
  return current.global;
}
export function isModeKilled(mode: string): boolean {
  return current.modes[mode] === true;
}
export function isAnimeBanned(id: number): boolean {
  return bannedAnimeSet.has(id);
}
export function isCharacterBanned(id: number): boolean {
  return bannedCharSet.has(id);
}
